import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isIOSDevice } from '../../utils/deviceDetection';
import { HapticEngine } from '../platform/HapticEngine';
import { TabButton, getPlatformSafeAreaInsets } from './BottomTabHelpers';
import './BottomTabs.css';

export interface TabItem {
  id: string;
  label: string;
  icon: string | React.ReactElement;
  activeIcon?: string | React.ReactElement;
  route: string;
  badge?: number | string;
  disabled?: boolean;
  ariaLabel?: string;
  isAction?: boolean;
}

interface BottomTabsProps {
  tabs: TabItem[];
  activeColor?: string;
  inactiveColor?: string;
  backgroundColor?: string;
  safeAreaBg?: string;
  hideShadow?: boolean;
  testID?: string;
}

/**
 * iOS-style Bottom Tab Navigation Component
 * 
 * Features:
 * - iOS Human Interface Guidelines compliant
 * - Haptic feedback on tab selection
 * - Badge notifications support
 * - Smooth animations and transitions
 * - Safe area handling for iOS
 * - Accessibility compliance (WCAG 2.1)
 * - Platform-specific styling
 * - Dynamic tab badge updates
 */
export const BottomTabs: React.FC<BottomTabsProps> = ({
  tabs,
  activeColor = '#007AFF', // iOS blue
  inactiveColor = 'var(--text-secondary, #6E6E73)', // Slate Gray
  backgroundColor = 'var(--bg-primary, #F9FAF9)',
  safeAreaBg = 'var(--bg-primary, #F9FAF9)',
  hideShadow = false,
  testID = 'bottom-tabs'
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('');
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [safeAreaInsets, setSafeAreaInsets] = useState({ bottom: 0 });

  // Determine active tab based on current route
  useEffect(() => {
    const currentTab = tabs.find(tab =>
      location.pathname === tab.route || location.pathname.startsWith(tab.route + '/')
    );
    if (currentTab) {
      setActiveTab(currentTab.id);
    }
  }, [location.pathname, tabs]);

  // Check platform and get safe area insets
  useEffect(() => {
    const isiOSPlatform = isIOSDevice();
    setIsIOS(isiOSPlatform);
    
    if (isiOSPlatform) {
      setSafeAreaInsets(getPlatformSafeAreaInsets());
    } else {
      setSafeAreaInsets({ bottom: 0 });
    }
  }, []);

  // Handle tab selection with haptic feedback
  const handleTabPress = useCallback(async (tab: TabItem) => {
    if (tab.disabled || activeTab === tab.id) return;

    // Trigger haptic feedback on iOS
    if (isIOS) {
      try {
        await HapticEngine.selection();
      } catch {
        // Silent fail for haptic feedback
      }
    }

    // Navigate to tab route
    navigate(tab.route);
    setActiveTab(tab.id);
    
    // Also explicitly scroll to top (especially for iOS)
    // This ensures scrolling happens even if navigating to the same route
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // For iOS, also scroll any scrollable containers
      const scrollableElements = [
        document.getElementById('root'),
        document.querySelector('.tab-content'),
        document.querySelector('main')
      ];
      
      scrollableElements.forEach(element => {
        if (element) {
          element.scrollTop = 0;
        }
      });
    }, 0);
  }, [activeTab, navigate, isIOS]);

  const tabBarStyle: React.CSSProperties = {
    backgroundColor,
    paddingBottom: isIOS ? safeAreaInsets.bottom : 0,
    boxShadow: hideShadow ? 'none' : '0 -1px 0 0 rgba(0, 0, 0, 0.12)'
  };

  const safeAreaStyle: React.CSSProperties = isIOS ? {
    backgroundColor: safeAreaBg,
    height: safeAreaInsets.bottom
  } : {};

  return (
    <>
      <nav
        className={`bottom-tabs ${isIOS ? 'ios' : 'android'}`}
        style={tabBarStyle}
        role="tablist"
        aria-label="Main navigation"
        data-testid={testID}
      >
        <div className="tabs-container">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              activeColor={activeColor}
              inactiveColor={inactiveColor}
              testID={testID}
              onPress={handleTabPress}
            />
          ))}
        </div>
      </nav>
      
      {/* iOS Safe Area Background */}
      {isIOS && safeAreaInsets.bottom > 0 && (
        <div 
          className="safe-area-bg" 
          style={safeAreaStyle}
          data-testid={`${testID}-safe-area`}
        />
      )}
    </>
  );
};

// Default tab icons (can be customized)
export const TabIcons = {
  home: '/icons/house.svg',
  homeActive: '/icons/house-fill.svg',
  search: '/icons/magnifying-glass.svg',
  searchActive: '/icons/magnifying-glass-fill.svg',
  favorites: '/icons/heart.svg',
  favoritesActive: '/icons/heart-fill.svg',
  profile: '/icons/person.svg',
  profileActive: '/icons/person-fill.svg',
  recipes: '/icons/book.svg',
  recipesActive: '/icons/book-fill.svg',
  mealPlan: '/icons/calendar.svg',
  mealPlanActive: '/icons/calendar-fill.svg',
  shoppingList: '/icons/list-bullet.svg',
  shoppingListActive: '/icons/list-bullet-fill.svg',
  community: '/icons/users.svg',
  communityActive: '/icons/users-fill.svg'
};

export default BottomTabs;